import { Component, OnInit, OnDestroy } from '@angular/core';

import { takeUntil, filter, map, distinctUntilChanged } from 'rxjs/operators';
import { MwAppConfigService } from '@core/services';
import { Subject } from 'rxjs';

import { MwUserService } from '@core/auth/services/user.service';
import { MwAppService } from './app.service';
import { MwCompanyService } from '@core/auth/services/company.service';
import { isEqual } from 'lodash';
import { HeaderModel, MwHttpConfigService, UrlPlaceholderModel } from '@core/http';
import { Config } from '@core/config';

@Component({
  selector: 'mw-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribeAll = new Subject<void>();

  constructor(
    private userService: MwUserService,
    private companyService: MwCompanyService,
    private appService: MwAppService,
    private appConfigService: MwAppConfigService,
    private httpConfigService: MwHttpConfigService
  ) {}

  ngOnInit(): void {
    this.subscribeToConfigChanges();
    this.subscribeToUserLanguageChange();
    this.subscribeToUserProfileChange();

    // Setup placeholders for HTTP service
    this.setHttpPlaceholders();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  private subscribeToConfigChanges(): void {
    this.appConfigService.config$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((config: Config) => this.onAppConfigChanged(config));
  }

  private subscribeToUserLanguageChange(): void {
    this.userService.userLanguage$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((language) => this.appService.changeLanguage(language));
  }

  private subscribeToUserProfileChange(): void {
    this.userService.userProfile$
      .pipe(
        takeUntil(this.unsubscribeAll),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        map(
          (profile) => profile && profile.hasAccessToModule && profile.companyId
        ),
        filter((companyId) => !!companyId)
      )
      .subscribe();
  }

  private onAppConfigChanged(config: Config): void {
    this.appService.setTheme(config.colorTheme);
    this.appService.setAvailableLanguages(
      config.languages,
      config.defaultLanguage
    );
  }

  private setHttpPlaceholders(): void {
    const companyPlaceholder: UrlPlaceholderModel = {
      name: 'companyId',
      value: this.companyService.activeCompanyId$,
    };
    var companyHeader: HeaderModel = {
      name: 'mw-company-id',
      value: this.companyService.activeCompanyId$,
    };

    this.httpConfigService.setConfig({
      urlPlaceholders: [companyPlaceholder],
      customHeaders: [companyHeader],
    });
  }
}
